import { trim } from "lodash";
import { useEffect, useState } from "react";
import { ApolloError, FetchPolicy } from "@apollo/client";
import {
  CallListType,
  UserDataFragment,
  useGetAllUsersQuery
} from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { TextField, useSnackbarsPipeline } from "@bonliva-crm/ui";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from "@material-ui/core";
import { UserSearchMulti } from "@consultants/consultant-profile-new/user-search/user-search-multi";
import { useUpdateCallListMembers } from "../hooks/update-call-list-members";
import { useUpdateCallListName } from "../hooks/update-call-list-name";
import { CallOrContactList } from "../utils";

type Props = {
  open: boolean;
  callList: CallOrContactList;
  type: CallListType;
  fetchPolicy?: FetchPolicy;
  onClose: () => void;
};

export function EditListModal(props: Props) {
  const classes = useStyles();
  const snack = useSnackbarsPipeline();
  const [selectedUsers, setSelectedUsers] = useState<UserDataFragment[]>([]);
  const [name, setName] = useState<string>(props.callList?.name);

  const isNameDirty = name !== props.callList?.name;

  const isMembersDirty =
    selectedUsers.length !== props.callList?.members.length ||
    !selectedUsers.every((user) =>
      props.callList.members.some((member) => member.PersonId === user._id)
    );

  useGetAllUsersQuery({
    fetchPolicy: props.fetchPolicy ?? "cache-and-network",
    skip: !props.callList,
    onCompleted: (data) => {
      setSelectedUsers(
        data.users.filter((user) =>
          props.callList.members.some(
            (selectedUser) => selectedUser.PersonId === user._id
          )
        )
      );
    }
  });

  useEffect(() => {
    if (props.open) {
      setName(props.callList?.name);
    }
  }, [props.open, props.callList]);

  const handleClose = () => {
    clearInputs();
    props.onClose();
  };

  const clearInputs = () => {
    setSelectedUsers([]);
    setName("");
  };

  const onError = (e: ApolloError) => {
    snack.showError(
      locale.updateError(
        (props.type === CallListType.CallList
          ? locale.CallList
          : locale.ContactList
        ).toLowerCase()
      )
    );
  };

  const onCompleted = () => {
    snack.showSuccess(locale.Saved);
    handleClose();
  };

  const { renameCallList } = useUpdateCallListName({ onError, onCompleted });
  const { updateCallListMembers } = useUpdateCallListMembers({ onError, onCompleted });

  const onSubmit = () => {
    isNameDirty && renameCallList({ callList: props.callList, newName: name });
    isMembersDirty && updateCallListMembers({ callList: props.callList, selectedUsers });
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { overflow: "visible" } }}
    >
      <DialogTitle>
        {`${locale.Edit} ${" "}
          ${(props.type === CallListType.CallList
            ? locale.CallList
            : locale.ContactList
          ).toLowerCase()}`}
      </DialogTitle>

      <DialogContent style={{ overflow: "visible" }}>
        <div className={classes.modalContents}>
          <TextField
            label={locale.Name}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            required={true}
          />
          <UserSearchMulti
            value={selectedUsers}
            onChange={setSelectedUsers}
            selectProps={{
              placeholder: null,
              inputLabel: locale.addPersons
            }}
          />
        </div>
      </DialogContent>

      <DialogActions>
        <Button variant="text" color="default" onClick={handleClose}>
          {locale.Cancel}
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={trim(name).length === 0 || (!isNameDirty && !isMembersDirty)}
        >
          {locale.Save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles((theme) => ({
  textField: {
    width: "100%",
    position: "relative",
    "&:hover": {
      cursor: "pointer"
    }
  },
  modalContents: {
    display: "flex",
    flexDirection: "column",
    rowGap: "1rem",
    height: "100%"
  }
}));
